import { useEffect, useState } from 'react';
import NotificationBell from '../components/NotificationBell';
import { getCategories, getProducts } from '../api';
import { getTelegram } from '../telegram';
import { WebActionBar, WebBackButton } from '../components/WebNav';
import type { Category, Product, CoffeeShop } from '../types';

// Эмодзи как фолбэк для категорий без загруженной картинки.
const CATEGORY_ICON: Record<number, string> = {
  1: '☕', 2: '🥛', 3: '🍯', 4: '🧁', 5: '📦', 6: '🌿',
};

const fmtPrice = (n: number) =>
  n.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const btnBase: React.CSSProperties = {
  border: 'none',
  borderRadius: 8,
  width: 32,
  height: 32,
  fontSize: 20,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  fontWeight: 700,
};

interface Props {
  onCartOpen: () => void;
  onHistory: () => void;
  add: (product: Product) => void;
  dec: (productId: string) => void;
  getQty: (productId: string) => number;
  totalItems: number;
  totalPrice: number;
  // Этап 19.5: режим заказа франшизы — точка, для которой network_manager оформляет
  // заказ, и возврат в кабинет. Для обычного клиента не передаются.
  contextShop?: CoffeeShop | null;
  onBackToCabinet?: () => void;
  // Этап W2 (веб): выход из веб-магазина обратно в кабинет/на форму входа.
  // Передаётся только в браузере (вне Telegram) — показывает кнопку «Выйти».
  onExit?: () => void;
  // Блок «Уведомления в приложении»: колокольчик → экран уведомлений.
  onNotifications?: () => void;
  // Блок D: переход на страницу «Депозит» и доступный остаток для шапки
  // (null — сумму не показываем: точек несколько или депозит ещё не загружен).
  onDeposit?: () => void;
  depositAvailable?: string | null;
}

export default function CatalogPage({ onCartOpen, onHistory, add, dec, getQty, totalItems, totalPrice, contextShop, onBackToCabinet, onExit, onDeposit, depositAvailable, onNotifications }: Props) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Category | null>(null);
  const [openProduct, setOpenProduct] = useState<Product | null>(null);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingProds, setLoadingProds] = useState(false);

  // Load categories once
  useEffect(() => {
    getCategories()
      .then(({ data }) => setCategories(data))
      .finally(() => setLoadingCats(false));
  }, []);

  // Telegram BackButton
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    // Если открыта модалка товара — Back её закрывает (не выходит из категории).
    if (openProduct) {
      tg.BackButton.show();
      const back = () => setOpenProduct(null);
      tg.BackButton.onClick(back);
      return () => { tg.BackButton.offClick(back); };
    }
    if (selected) {
      tg.BackButton.show();
      const back = () => setSelected(null);
      tg.BackButton.onClick(back);
      return () => { tg.BackButton.offClick(back); tg.BackButton.hide(); };
    }
    // Этап 19.5: на корне каталога в режиме франшизы Back ведёт обратно в кабинет.
    if (onBackToCabinet) {
      tg.BackButton.show();
      const back = () => onBackToCabinet();
      tg.BackButton.onClick(back);
      return () => { tg.BackButton.offClick(back); tg.BackButton.hide(); };
    }
    tg.BackButton.hide();
  }, [selected, openProduct, onBackToCabinet]);

  // Telegram MainButton — открывает корзину
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    if (totalItems > 0) {
      tg.MainButton.setText(`Корзина · ${totalItems} шт · ${fmtPrice(totalPrice)}`);
      tg.MainButton.show();
      tg.MainButton.onClick(onCartOpen);
      return () => { tg.MainButton.offClick(onCartOpen); tg.MainButton.hide(); };
    } else {
      tg.MainButton.hide();
    }
  }, [totalItems, totalPrice, onCartOpen]);

  const openCategory = (cat: Category) => {
    setSelected(cat);
    setProducts([]);
    setLoadingProds(true);
    getProducts(cat.id)
      .then(({ data }) => setProducts(data))
      .finally(() => setLoadingProds(false));
  };

  const handleAdd = (p: Product) => {
    add(p);
    getTelegram()?.HapticFeedback?.impactOccurred('light');
  };

  const handleDec = (p: Product) => {
    dec(p.id);
    getTelegram()?.HapticFeedback?.impactOccurred('light');
  };

  // Этап 19.5: баннер «для какой точки оформляется заказ» (режим франшизы).
  const contextBanner = contextShop ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '10px 12px',
        marginBottom: 16,
        borderRadius: 10,
        background: 'var(--tg-secondary-bg)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="hint" style={{ fontSize: 11 }}>🏪 Заказ для точки</div>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>{contextShop.name}</div>
      </div>
      {onBackToCabinet && (
        <button
          onClick={onBackToCabinet}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--tg-button)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
          }}
        >
          ‹ Кабинет
        </button>
      )}
    </div>
  ) : null;

  // ── Список товаров ──────────────────────────────────────────────
  if (selected) {
    return (
      <>
        <div className="container" style={{ padding: '16px 16px 80px' }}>
          {contextBanner}
          <WebBackButton onClick={() => setSelected(null)} label="‹ К категориям" />
          <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>
            {CATEGORY_ICON[selected.code] ?? '🛒'} {selected.name}
          </h3>

          {loadingProds ? (
            <p className="hint">Загрузка товаров…</p>
          ) : products.length === 0 ? (
            <p className="hint">В этой категории пока нет товаров</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {products.map((p) => {
                const qty = getQty(p.id);
                return (
                  <div
                    key={p.id}
                    className="card product-card"
                    onClick={() => setOpenProduct(p)}
                    role="button"
                  >
                    <div className="product-thumb">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} />
                      ) : (
                        <div className="product-thumb-placeholder">
                          {CATEGORY_ICON[selected.code] ?? '🍽'}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                      {p.short_name && (
                        <div className="hint" style={{ marginTop: 2 }}>{p.short_name}</div>
                      )}
                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="hint">
                          {p.weight_grams ? `${p.weight_grams} г` : p.article}
                        </span>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                          onClick={e => e.stopPropagation()}
                        >
                          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--tg-button)', marginRight: 8 }}>
                            {fmtPrice(Number(p.price_with_vat))}
                          </span>
                          {qty === 0 ? (
                            <button
                              onClick={() => handleAdd(p)}
                              style={{ ...btnBase, background: 'var(--tg-button)', color: '#fff' }}
                              aria-label="Добавить в корзину"
                            >
                              +
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleDec(p)}
                                style={{ ...btnBase, background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)' }}
                                aria-label="Убрать одну штуку"
                              >
                                −
                              </button>
                              <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{qty}</span>
                              <button
                                onClick={() => handleAdd(p)}
                                style={{ ...btnBase, background: 'var(--tg-button)', color: '#fff' }}
                                aria-label="Добавить ещё одну штуку"
                              >
                                +
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {openProduct && (
          <ProductDetailsModal
            product={openProduct}
            qty={getQty(openProduct.id)}
            onClose={() => setOpenProduct(null)}
            onAdd={() => handleAdd(openProduct)}
            onDec={() => handleDec(openProduct)}
          />
        )}

        {totalItems > 0 && (
          <WebActionBar
            label={`Корзина · ${totalItems} шт · ${fmtPrice(totalPrice)}`}
            onClick={onCartOpen}
          />
        )}
      </>
    );
  }

  // ── Сетка категорий ─────────────────────────────────────────────
  return (
    <>
    <div className="container" style={{ padding: '16px 16px 80px' }}>
      {contextBanner}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: '0 0 2px', fontSize: 22 }}>GreenHouse</h2>
          <p className="hint" style={{ margin: 0 }}>Оптовая доставка в кофейни</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
          {/* Блок «Уведомления в приложении»: колокольчик со счётчиком непрочитанных */}
          {onNotifications && <NotificationBell onOpen={onNotifications} />}
          {/* Блок D: депозит — доступный остаток и переход на страницу «Депозит» */}
          {onDeposit && (
            <button
              onClick={onDeposit}
              style={{
                background: 'var(--tg-secondary-bg)',
                border: 'none',
                borderRadius: 10,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--tg-button)',
                fontFamily: 'inherit',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              💳 {depositAvailable != null ? fmtPrice(Number(depositAvailable)) : 'Депозит'}
            </button>
          )}
          <button
            onClick={onHistory}
            style={{
              background: 'var(--tg-secondary-bg)',
              border: 'none',
              borderRadius: 10,
              padding: '6px 12px',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tg-hint)',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            Заказы
          </button>
          {/* Этап W2 (веб): выход из веб-магазина. В Telegram onExit не передаётся. */}
          {onExit && (
            <button
              onClick={onExit}
              style={{
                background: 'transparent',
                border: '1px solid var(--tg-hint)',
                borderRadius: 10,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--tg-hint)',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Выйти
            </button>
          )}
        </div>
      </div>

      {loadingCats ? (
        <p className="hint">Загрузка категорий…</p>
      ) : categories.length === 0 ? (
        <p className="hint">Категории не найдены</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {categories.map((cat) => {
            const hasImg = !!cat.image_url;
            return (
              <button
                key={cat.id}
                onClick={() => openCategory(cat)}
                className={`category-tile ${hasImg ? 'category-tile--image' : ''}`}
                style={
                  hasImg
                    ? { backgroundImage: `url(${cat.image_url})` }
                    : undefined
                }
              >
                {!hasImg && (
                  <div className="category-tile-icon">
                    {CATEGORY_ICON[cat.code] ?? '🛒'}
                  </div>
                )}
                <span className="category-tile-label">{cat.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>

    {totalItems > 0 && (
      <WebActionBar
        label={`Корзина · ${totalItems} шт · ${fmtPrice(totalPrice)}`}
        onClick={onCartOpen}
      />
    )}
    </>
  );
}

/* ===================================================================
 * Модалка с предпросмотром товара: квадратное фото + описание/КБЖУ
 * ================================================================ */

function ProductDetailsModal({
  product, qty, onClose, onAdd, onDec,
}: {
  product: Product;
  qty: number;
  onClose: () => void;
  onAdd: () => void;
  onDec: () => void;
}) {
  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div className="product-modal" onClick={e => e.stopPropagation()}>
        <button className="product-modal-close" onClick={onClose} aria-label="Закрыть">✕</button>

        <div className="product-modal-image">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} />
          ) : (
            <div className="product-modal-image-placeholder">🍽</div>
          )}
        </div>

        <div className="product-modal-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.25 }}>{product.name}</div>
              {product.short_name && (
                <div className="hint" style={{ marginTop: 2 }}>{product.short_name}</div>
              )}
              <div className="hint" style={{ marginTop: 4 }}>
                {product.weight_grams} г · артикул {product.article}
              </div>
            </div>
            <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--tg-button)', whiteSpace: 'nowrap' }}>
              {fmtPrice(Number(product.price_with_vat))}
            </div>
          </div>

          {product.description && (
            <div className="product-modal-description">
              {product.description}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
            {qty === 0 ? (
              <button
                onClick={onAdd}
                style={{
                  flex: 1,
                  background: 'var(--tg-button)', color: '#fff',
                  border: 'none', borderRadius: 10, padding: '12px 16px',
                  fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Добавить в корзину
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'space-between' }}>
                <span className="hint">В корзине</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={onDec}
                    style={{ ...btnBase, width: 40, height: 40, background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)' }}
                    aria-label="Убрать одну штуку"
                  >
                    −
                  </button>
                  <span style={{ minWidth: 32, textAlign: 'center', fontWeight: 800, fontSize: 18 }}>{qty}</span>
                  <button
                    onClick={onAdd}
                    style={{ ...btnBase, width: 40, height: 40, background: 'var(--tg-button)', color: '#fff' }}
                    aria-label="Добавить ещё одну штуку"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
